import queryString from "query-string";
import { PureComponent } from "react";

import * as CalculatorStepTracker from "@/services/tracking/trackers/tools/calculators/calculatorStep.tracker";
import { CALCULATOR_KEYS } from "@/services/tracking/trackers/tools/calculators/config";
import { asyncState, validNumber } from "@/utils/calculators";

import Calculation from "./calculation/calculation.component";
import Result from "./result/result.component";

type GlassBrickCalculatorState = {
    wallLength: number;
    wallHeight: number;
    glassBrickWidth: number;
    glassBrickLength: number;
    glassBrickHeight: number;
    wallArea: number;
    glassBrickArea: number;
    numberOfGlassBricks: number;
    numberOfGlassBrickCrosses: number;
    mortarVolume: number;
    jointCementVolume: number;
};

class GlassBrickCalculator extends PureComponent<
    {},
    GlassBrickCalculatorState
> {
    query = queryString.parse(location.search);

    constructor(props: {}) {
        super(props);

        this.state = {
            // calculationProps
            wallLength: this.query.wallLength
                ? Number(this.query.wallLength)
                : 0,
            wallHeight: this.query.wallHeight
                ? Number(this.query.wallHeight)
                : 0,
            glassBrickWidth: this.query.glassBrickWidth
                ? Number(this.query.glassBrickWidth)
                : 0,
            glassBrickLength: this.query.glassBrickLength
                ? Number(this.query.glassBrickLength)
                : 0,
            glassBrickHeight: this.query.glassBrickHeight
                ? Number(this.query.glassBrickHeight)
                : 0,

            // resultProps
            wallArea: 0,
            glassBrickArea: 0,
            numberOfGlassBricks: 0,
            numberOfGlassBrickCrosses: 0,
            mortarVolume: 0,
            jointCementVolume: 0
        };
    }

    async componentDidMount() {
        await this.calculateFields(true);
    }

    // calculations
    wallArea = () => {
        let wallArea = 0;

        const wallLength = this.state.wallLength;

        const wallHeight = this.state.wallHeight;

        if (validNumber(wallLength) && validNumber(wallHeight)) {
            wallArea = wallLength * wallHeight;
        }

        return wallArea;
    };
    glassBrickArea = () => {
        let glassBrickArea = 0;

        const glassBrickLength = this.state.glassBrickLength;

        const glassBrickHeight = this.state.glassBrickHeight;

        if (validNumber(glassBrickLength) && validNumber(glassBrickHeight)) {
            glassBrickArea =
                (glassBrickLength + 1) * (glassBrickHeight + 1) * 0.0001;
        }

        return glassBrickArea;
    };
    numberOfGlassBricks = () => {
        let numberOfGlassBricks = 0;

        const wallArea = this.state.wallArea;

        const glassBrickArea = this.state.glassBrickArea;

        if (validNumber(wallArea) && validNumber(glassBrickArea)) {
            numberOfGlassBricks = Number(Math.ceil(wallArea / glassBrickArea));
        }

        return Number(numberOfGlassBricks);
    };
    numberOfGlassBrickCrosses = () => {
        const wallHeight = this.state.wallHeight;

        const brickHeight = this.state.glassBrickHeight * 0.01;
        const numberOfGlassbricksHigh = Math.ceil(wallHeight / brickHeight);

        const wallLength = this.state.wallLength;

        const brickLength = this.state.glassBrickLength * 0.01;
        const numberOfGlassbricksLong = Math.ceil(wallLength / brickLength);

        const numberOfGlassbrickCrosses = Number(
            (numberOfGlassbricksHigh + 1) * (numberOfGlassbricksLong + 1)
        );

        return validNumber(numberOfGlassbrickCrosses)
            ? numberOfGlassbrickCrosses
            : 0;
    };
    mortarVolume = () => {
        let mortarVolume = 0;

        const brickLength = this.state.glassBrickLength;

        const brickWidth = this.state.glassBrickWidth;

        const brickHeight = this.state.glassBrickHeight;

        const brickVolume =
            brickLength * brickWidth * brickHeight * 0.001 * 0.001;

        const volumeTotalBricks = brickVolume * this.state.numberOfGlassBricks;

        const wallVolume = this.state.wallArea * brickWidth * 0.01;

        if (validNumber(wallVolume) && validNumber(volumeTotalBricks)) {
            mortarVolume = wallVolume - volumeTotalBricks;
        }

        return mortarVolume;
    };
    joinCementVolume = () => {
        const areaJointCement = 0.01 * 0.01;

        const numberOfBricks = this.state.numberOfGlassBricks;

        const brickHeight = this.state.glassBrickHeight / 100;

        const brickLength = this.state.glassBrickLength / 100;
        const brickDeadSpace = areaJointCement * 0.01;

        const joinCementVolume =
            (brickLength * numberOfBricks * areaJointCement +
                brickHeight * numberOfBricks * areaJointCement +
                brickDeadSpace * numberOfBricks) *
            2;

        return validNumber(joinCementVolume) ? joinCementVolume : 0;
    };

    // setters
    setBrickMeasurements = (length: number, width: number, height: number) => {
        this.setState(
            {
                glassBrickLength: length,
                glassBrickWidth: width,
                glassBrickHeight: height
            },
            () => this.calculateFields()
        );
    };
    setCalculationValue = (field: string, value: string) => {
        const numberValue = parseFloat(value.replace(",", "."));
        this.setState(
            (state) => ({ ...state, [field]: numberValue }),
            async () => {
                await this.calculateFields();
            }
        );
    };
    calculateFields = async (initialCall?: boolean) => {
        await asyncState(this, {
            wallArea: this.wallArea(),
            glassBrickArea: this.glassBrickArea(),
            numberOfGlassBrickCrosses: this.numberOfGlassBrickCrosses()
        });
        await asyncState(this, {
            numberOfGlassBricks: this.numberOfGlassBricks()
        });
        await asyncState(this, {
            mortarVolume: this.mortarVolume(),
            jointCementVolume: this.joinCementVolume()
        });
        if (initialCall) {
            CalculatorStepTracker.trackStart(CALCULATOR_KEYS.glassBrick);
        } else {
            CalculatorStepTracker.trackSubmit(CALCULATOR_KEYS.glassBrick);
        }
    };

    render() {
        const calculationProps = {
            setCalculationValue: this.setCalculationValue,
            setBrickMeasurements: this.setBrickMeasurements,
            wallLength: this.state.wallLength,
            wallHeight: this.state.wallHeight,
            glassBrickLength: this.state.glassBrickLength,
            glassBrickWidth: this.state.glassBrickWidth,
            glassBrickHeight: this.state.glassBrickHeight
        };
        const resultsProps = {
            wallArea: this.state.wallArea,
            glassBrickArea: this.state.glassBrickArea,
            numberOfGlassBricks: this.state.numberOfGlassBricks,
            numberOfGlassBrickCrosses: this.state.numberOfGlassBrickCrosses,
            mortarVolume: this.state.mortarVolume,
            jointCementVolume: this.state.jointCementVolume,
            mailParams: {
                wallLength: this.state.wallLength,
                wallHeight: this.state.wallHeight,
                glassBrickLength: this.state.glassBrickLength,
                glassBrickWidth: this.state.glassBrickWidth,
                glassBrickHeight: this.state.glassBrickHeight
            }
        };

        return (
            <div id="calculator">
                <Calculation {...calculationProps} />
                <Result {...resultsProps} />
            </div>
        );
    }
}

export { GlassBrickCalculator };
